/**
 * songRegistryModel — pure logic for the canonical `songs` registry (no I/O).
 *
 * DECIDED 2026-08-05 (repertoire arc, question A): song identity is a
 * STUDIO-OWNED id — a `songs` doc id — never an external id and never a title.
 * Both alternatives were measured, not assumed:
 *
 *  - MusicBrainz work ids: 4 of 7 musical-theatre recordings carried no work
 *    relation at all, and title-only work matching returned the wrong song
 *    entirely ("She Used to Be Mine" → the Spin Doctors track). Apple-sourced
 *    adds carry no MB id. An id that exists for half the catalog is not an id.
 *  - Titles: teachers type shorthand mid-lesson ("goodbye yellow" is a live
 *    prod row), so titles miss joins — and identical titles on different
 *    songs create false ones. A title-as-key can never be split or renamed.
 *
 * External ids are therefore EVIDENCE for matching, never the key. When the
 * evidence is too weak to match (shorthand adds with no metadata), the
 * repertoire row stays UNLINKED (canonicalSongId: null) rather than guessed —
 * an empty field is honest and visibly needs filling; a confident wrong one
 * gives no reason to doubt it.
 *
 * Everything here is pure so the matching rules are unit-testable without
 * Firestore; SongRegistryService owns the reads and writes.
 */

const UUID_RE =
	/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const APPLE_TRACK_PREFIX = 'apple-track-';

/** Same normalization as repertoire.songNameLower — keep these in lockstep. */
export const normalizeTitle = (value) => (value || '').trim().toLowerCase();

/**
 * Match precedence, strongest evidence first. A work id identifies the SONG
 * (every recording of it shares one), a recording/track id identifies one
 * recording of it, and title+artist is the weakest fallback.
 */
export const REF_MATCH_ORDER = [
	'musicbrainzWorkId',
	'musicbrainzRecordingId',
	'appleTrackId',
];

/**
 * Pull external ids out of a repertoire `metadata` blob into registry shape.
 * The blob's fields are source-dependent (observed in prod, 2026-08-05):
 * Apple-sourced rows have `recordingId: "apple-track-<n>"` and often
 * `musicbrainzId: null`; MusicBrainz-sourced rows have `musicbrainzId` (and
 * sometimes `recordingId` duplicating it); `workId` is present only when the
 * recording carried a work relation.
 */
export const extractExternalRefs = (metadata) => {
	if (!metadata) return {};
	const refs = {};
	if (metadata.workId) refs.musicbrainzWorkId = metadata.workId;
	if (metadata.musicbrainzId) refs.musicbrainzRecordingId = metadata.musicbrainzId;
	const rec = metadata.recordingId;
	if (typeof rec === 'string') {
		if (rec.startsWith(APPLE_TRACK_PREFIX)) {
			refs.appleTrackId = rec;
		} else if (!refs.musicbrainzRecordingId && UUID_RE.test(rec)) {
			refs.musicbrainzRecordingId = rec;
		}
	}
	return refs;
};

/**
 * Case-insensitive artist comparison for the title+artist fallback. Two empty
 * artists do NOT match — that would join every artistless row sharing a title,
 * which is exactly the false-join failure the studio-owned id exists to avoid.
 */
export const artistMatches = (a, b) => {
	const na = normalizeTitle(a);
	return na !== '' && na === normalizeTitle(b);
};

/** The registry doc for a freshly minted canonical song. */
export const buildSongDoc = (metadata) => ({
	title: (metadata.title || '').trim(),
	titleLower: normalizeTitle(metadata.title),
	artist: (metadata.artist || '').trim(),
	externalRefs: extractExternalRefs(metadata),
});

/**
 * Field-path patch adding refs a matched registry doc is missing. Only ABSENT
 * refs are filled — an existing value is never overwritten, because a human
 * may have corrected it and because two differing recording ids can both be
 * true (different recordings of one song); first-in wins, merge tooling can
 * revisit. Returns null when there is nothing to add.
 */
export const refsBackfillPatch = (existingRefs, incomingRefs) => {
	const patch = {};
	for (const key of REF_MATCH_ORDER) {
		if (incomingRefs[key] && !(existingRefs || {})[key]) {
			patch[`externalRefs.${key}`] = incomingRefs[key];
		}
	}
	return Object.keys(patch).length ? patch : null;
};
